import SearchResults from "components/searchResults/SearchResults";
import DomainSearchBox, {
  isValidDomain,
} from "components/domainSearchBox/DomainSearchBox";
import { useAppDispatch, useAppSelector } from "hooks";
import { memo, useEffect, useState } from "react";
import domainSlice from "reducers/domainSlice";
import {
  domainSearchingSelector,
  searchResultSelector,
  newDomainsSelector,
  gettingNewDomainsSelector,
} from "selectors/domainSelectors";
import { RootState } from "store";
import Page from "components/page/Page";
import Helmet from "components/headHelmet/HeadHelmet";
import styled from "styled-components";
import { getSiteUrl, isPrerendering } from "utility/urlUtility";
import NewDomainsGrid from "components/newDomainsGrid/NewDomainsGrid";
import { useTranslation } from "react-i18next";

const StyledSearchPanel = styled.div`
  margin: 0 10px;
  width: 100%;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: 30px;
  max-width: 800px;
`;

const MainHeading = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #222222;
  margin-bottom: 15px;
  line-height: 1.2;

  @media only screen and (max-width: 768px) {
    font-size: 1.5rem;
  }

  @media only screen and (max-width: 480px) {
    font-size: 1.35rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 0.95rem;
  color: #555555;
  line-height: 1.6;
  margin-bottom: 0;

  @media only screen and (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const HomePage = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("home");

  const [searchValue, setSearchValue] = useState("");
  const [hasSearchPhrase, setHasSearchPhrase] = useState(false);

  const searchResult = useAppSelector((state: RootState) =>
    searchResultSelector(state),
  );

  const domainSearching = useAppSelector((state: RootState) =>
    domainSearchingSelector(state),
  );

  const newDomains = useAppSelector((state: RootState) =>
    newDomainsSelector(state),
  );

  const gettingNewDomains = useAppSelector((state: RootState) =>
    gettingNewDomainsSelector(state),
  );

  useEffect(() => {
    // Skip API calls during pre-rendering
    if (isPrerendering()) {
      return;
    }
    dispatch(domainSlice.actions.getNewDomains());
  }, [dispatch]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    if (isValidDomain(value)) {
      setHasSearchPhrase(value.length > 0);
      dispatch(domainSlice.actions.searchDomain(value));
    } else if (value === "") {
      setHasSearchPhrase(false);
    }
  };

  const pageUrl = `${getSiteUrl()}/`;

  const metaTitle = t("metaTitle");
  const metaDescription = t("metaDescription");

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:site_name" content="Domain Findr" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content={metaTitle} />
        <meta property="twitter:description" content={metaDescription} />

        {/* Additional SEO */}
        <meta name="keywords" content={t("metaKeywords")} />
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
      </Helmet>
      <Page collapsedView={hasSearchPhrase && searchResult !== undefined}>
        <StyledSearchPanel>
          {!(hasSearchPhrase && searchResult !== undefined) && (
            <HeroSection>
              <MainHeading>{t("hero.heading")}</MainHeading>
              <HeroDescription>{t("hero.description")}</HeroDescription>
            </HeroSection>
          )}
          <DomainSearchBox
            value={searchValue}
            onChange={handleSearchChange}
            loading={domainSearching}
          />
          {!(hasSearchPhrase && searchResult !== undefined) && (
            <NewDomainsGrid
              domains={newDomains || []}
              loading={gettingNewDomains || isPrerendering()}
            />
          )}
        </StyledSearchPanel>
        {hasSearchPhrase && <SearchResults searchResult={searchResult} />}
      </Page>
    </>
  );
};

export default memo(HomePage);
